import React, { Fragment, useState, useEffect, useRef } from "react";
import styled from "styled-components";
import {
  IconButton,
  Popover,
  Tooltip,
  CircularProgress,
  Button,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl
} from "@material-ui/core";
import { AssignmentInd, Close, FilterList, Search, KeyboardArrowLeft, KeyboardArrowRight } from "@material-ui/icons";
import ApiClient from "utils/ApiClient";

const TEXT_TYPES = ["1", "14", "8", "20", "24", "3", "11", "13"];
const NUMBER_TYPES = ["5", "17", "22", "7", "19"];

const ChooseDonor = ({ donorProps }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [donors, setDonors] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigningDonor, setAssigningDonor] = useState(false);
  
  // Pagination State
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const LIMIT = 20;

  const [filters, setFilters] = useState({});

  useEffect(() => {
    return () => {
      setFilters({});
    };
  }, []);

  const DONOR_LIST_ID = "90663108";
  const DONOR_VIEW_ID = "287";

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    // Fetch initial data (Page 1)
    fetchDonors({}, 1); 
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "choose-donor-popover" : undefined;

  const buildCriteria = (currentFilters) => {
    const criteriaParts = [];
    
    Object.keys(currentFilters).forEach((colId) => {
      const filter = currentFilters[colId];
      const column = columns.find(c => String(c.id) === String(colId));
      
      if (!filter.value || !column) return;

      const alias = column.alias_path;

      if (filter.type === 'text') {
          criteriaParts.push(`${alias} LIKE '%${filter.value}%'`);
      } else {
          criteriaParts.push(`${alias} ${filter.operator} "${filter.value}"`);
      }
    });

    return criteriaParts.join(" AND ");
  };

  // Modified fetch to accept pageToLoad
  const fetchDonors = async (activeFilters = filters, pageToLoad = 1) => {
    setLoading(true);
    try {
      const criteria = buildCriteria(activeFilters);
      
      // Pagination Logic
      const start = (pageToLoad - 1) * LIMIT;

      const payload = {
        list_id: DONOR_LIST_ID,
        view_id: DONOR_VIEW_ID,
        limit: LIMIT,
        page: pageToLoad,
        start: start,
      };

      if (criteria) {
        payload.criteria = criteria;
      }

      const response = await ApiClient.Views.getViewData(payload);

      if (response.success && response.data) {
        setDonors(response.data.data);
        
        // Handle Total Count from response
        const count = parseInt(response.totalCount || response.data.totalCount || 0);
        setTotalCount(count);
        
        // Update current page state
        setPage(pageToLoad);

        const sortedColumns = (response.data.columns || []).sort(
          (a, b) => a.order - b.order
        );
        setColumns(sortedColumns);
      }
    } catch (error) {
      console.error("Error fetching donors:", error);
      window.appStore.notificationStore.showNotification("Failed to fetch donor list", "error");
    } finally {
      setLoading(false);
    }
  };

  const onApplyFilter = (colId, value, operator, type) => {
    const newFilters = { ...filters };
    
    if (!value) {
        delete newFilters[colId];
    } else {
        newFilters[colId] = { value, operator, type };
    }
    
    setFilters(newFilters);
    // Always reset to Page 1 when filtering
    fetchDonors(newFilters, 1);
  };

  const handlePageChange = (newPage) => {
      if (newPage < 1) return;
      fetchDonors(filters, newPage);
  };

  const handleSelectDonor = async (donor) => {
    const targetRowId = donorProps.data?.row_id;
    const donorName = donor['column_991'];

    if(!targetRowId || !donorName) {
        return;
    }

    try {
        window.appStore.notificationStore.showNotification(`Assigning ${donorName}...`, "info");
        setAssigningDonor(true);

        const resp = await ApiClient.List.addEditCell({
            list_id: 59289877,
            row_id: targetRowId,
            column_id: 1142,
            value: JSON.stringify({
                reference_column_id: donor['row_id'], 
                value: donorName
            }),
            parent_row_id: 0,
            parent_list_id: 0,
            parent_column_id: 0,
            is_web: 1,
        });

        if (resp.success) {
            window.appStore.notificationStore.showNotification(`Successfully assigned ${donorName}`, "success");
            if(donorProps.store) {
                donorProps.store?.refreshData();
            }
            handleClose(); 
        } else {
            window.appStore.notificationStore.showNotification(resp.message || "Failed to update donor.", "error");
        }
        setAssigningDonor(false);

    } catch (error) {
        console.error("Error in handleSelectDonor:", error);
        window.appStore.notificationStore.showNotification("An error occurred while assigning the donor.", "error");
        setAssigningDonor(false);
    }
  };

  const renderCellData = (row, col) => {
    const value = row[col.dataIndex];
    if (value === null || value === undefined) return "";

    const numericTypes = ["17", "19", "7", "5", "22"]; 
    if (numericTypes.includes(String(col.column_type_id))) {
        const num = parseFloat(value);
        if (!isNaN(num)) {
             return `₹${num.toLocaleString('en-IN')}`;
        }
    }
    return value;
  };

  // Pagination Calculations
  const totalPages = Math.ceil(totalCount / LIMIT);
  const startRecord = (page - 1) * LIMIT + 1;
  const endRecord = Math.min(page * LIMIT, totalCount);

  return (
    <Fragment>
      <Tooltip title="Choose Donor">
        <StyledButton onClick={handleClick}>
            <StyledIconButton size="small">
                <StyledDonorIcon />
            </StyledIconButton>
            Choose Donor
        </StyledButton>
      </Tooltip>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
            style: { width: '80%', maxHeight: '90%', minHeight: '70%', display: 'flex', flexDirection: 'column' } 
        }}
      >
        <StyledContainer>
          <StyledHeader>
            <Typography variant="h6">Select a Donor</Typography>
            <div>
                {(filters && Object.keys(filters).length > 0 && !loading) && 
                    <Button 
                        variant="contained" 
                        color="inherit" 
                        size="small"
                        style={{marginRight: 15}}
                        onClick={() => {
                            setFilters({});
                            fetchDonors({}, 1); // Reset to page 1 on clear
                        }}
                    >
                        Clear All Filters
                    </Button>
                }
                <IconButton size="small" onClick={handleClose}>
                    <Close />
                </IconButton>
            </div>
          </StyledHeader>

          <StyledContent>
            {loading ? (
              <LoadingWrapper>
                <CircularProgress />
              </LoadingWrapper>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table stickyHeader size="small" aria-label="donor table">
                  <TableHead>
                    <TableRow>
                      <StyledTableCell style={{ minWidth: '80px' }}>Action</StyledTableCell>
                      <StyledTableCell style={{ minWidth: '50px' }}>ID</StyledTableCell>
                      
                      {columns.map((col) => (
                        <ColumnHeader 
                            key={col.id} 
                            column={col} 
                            onFilterApply={onApplyFilter}
                            activeFilter={filters[col.id]}
                        />
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                        <TableRow>
                            <TableCell colSpan={columns.length + 2} align="center">
                                <CircularProgress size={24} /> Updating results...
                            </TableCell>
                        </TableRow>
                    ) : (
                        donors.length > 0 ? (
                            donors.map((donor) => (
                                <StyledTableRow key={donor.row_id}>
                                <TableCell>
                                    <Button 
                                        variant="contained" 
                                        color="primary" 
                                        size="small"
                                        onClick={() => handleSelectDonor(donor)}
                                        disabled={assigningDonor}
                                    >
                                        Select
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    {donor['row_id']}
                                </TableCell>

                                {columns.map((col) => (
                                    <TableCell key={`${donor.row_id}-${col.id}`}>
                                    {renderCellData(donor, col)}
                                    </TableCell>
                                ))}
                                </StyledTableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length + 2} align="center">
                                No donors found matching your criteria.
                                </TableCell>
                            </TableRow>
                        )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </StyledContent>

          {/* Pagination Footer */}
          {!loading && totalCount > 0 && (
            <StyledPagination>
                <div style={{marginRight: 'auto'}}>
                    <Typography variant="body2" color="textSecondary">
                        Showing {startRecord}-{endRecord} of {totalCount} records
                    </Typography>
                </div>
                <div style={{display: 'flex', alignItems: 'center'}}>
                    <IconButton 
                        size="small" 
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page === 1}
                    >
                        <KeyboardArrowLeft />
                    </IconButton>
                    <Typography variant="body2" style={{margin: '0 10px'}}>
                        Page {page} of {totalPages}
                    </Typography>
                    <IconButton 
                        size="small" 
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page >= totalPages}
                    >
                        <KeyboardArrowRight />
                    </IconButton>
                </div>
            </StyledPagination>
          )}

        </StyledContainer>
      </Popover>
    </Fragment>
  );
};

const ColumnHeader = ({ column, onFilterApply, activeFilter }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [tempValue, setTempValue] = useState(activeFilter ? activeFilter.value : "");
    const [operator, setOperator] = useState(activeFilter ? activeFilter.operator : "=");

    const colType = String(column.column_type_id);
    const isText = TEXT_TYPES.includes(colType);
    const isNumber = NUMBER_TYPES.includes(colType);

    if (!isText && !isNumber) {
        return <StyledTableCell style={{ minWidth: column.col_width || 'auto' }}>{column.header ?? column.name}</StyledTableCell>;
    }

    const handleOpen = (e) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleApply = (e) => {
        if(e) e.preventDefault();
        
        let finalOp = operator;
        let type = 'text';

        if (isText) {
            finalOp = 'LIKE';
            type = 'text';
        } else {
            type = 'number';
        }

        onFilterApply(column.id, tempValue, finalOp, type);
        handleClose();
    };

    const handleClear = () => {
        setTempValue("");
        onFilterApply(column.id, "", operator, isText ? 'text' : 'number');
        handleClose();
    };

    const open = Boolean(anchorEl);

    return (
        <StyledTableCell style={{ minWidth: column.col_width || 'auto' }}>
            <HeaderContent style={{color: activeFilter ? "blue" : "black"}}>
                <span>{column.header ?? column.name}</span>
                <IconButton size="small" onClick={handleOpen}>
                    <FilterList fontSize="small" style={{color: activeFilter ? "blue" : "black"}} />
                </IconButton>
            </HeaderContent>
            
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <FilterPadding>
                    <form onSubmit={handleApply}>
                        <Grid container spacing={2} direction="column" style={{minWidth: '220px'}}>
                            
                            {isNumber && (
                                <Grid item>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Operator</InputLabel>
                                        <Select
                                            value={operator}
                                            onChange={(e) => setOperator(e.target.value)}
                                            label="Operator"
                                        >
                                            <MenuItem value="=">Equals (=)</MenuItem>
                                            <MenuItem value="<>">Not Equals (&lt;&gt;)</MenuItem>
                                            <MenuItem value=">">Greater Than (&gt;)</MenuItem>
                                            <MenuItem value=">=">Greater Than Equal (&ge;)</MenuItem>
                                            <MenuItem value="<">Less Than (&lt;)</MenuItem>
                                            <MenuItem value="<=">Less Than Equal (&le;)</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}

                            <Grid item>
                                <TextField 
                                    label={isText ? "Search Text" : "Value"}
                                    variant="outlined" 
                                    size="small" 
                                    fullWidth
                                    value={tempValue}
                                    onChange={(e) => setTempValue(e.target.value)}
                                    placeholder={isText ? "Type to search..." : "e.g. 50000"}
                                />
                            </Grid>
                            
                            <Grid item container justify="space-between">
                                <Button size="small" onClick={handleClear}>Clear</Button>
                                <Button 
                                    type="submit" 
                                    size="small" 
                                    variant="contained" 
                                    color="primary"
                                    startIcon={<Search style={{color: '#FFF'}}/>}
                                >
                                    Search
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                </FilterPadding>
            </Popover>
        </StyledTableCell>
    );
};

export default ChooseDonor;

// --- Styles ---

const StyledIconButton = styled(IconButton)`
  && {
    padding: 0px;
    margin: 0px 8px 0px 0px;
  }
`;

const StyledDonorIcon = styled(AssignmentInd)`
  && {
    color: #4caf50; 
  }
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
`;

const StyledContent = styled.div`
  flex: 1;
  overflow: auto;
  margin-bottom: 10px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  width: 100%;
`;

const StyledTableCell = styled(TableCell)`
  && {
    font-weight: bold;
    background-color: #f5f5f5;
    color: #333;
    white-space: nowrap;
  }
`;

const StyledTableRow = styled(TableRow)`
  &:hover {
    background-color: #f9f9f9;
  }
`;

const StyledButton = styled(Button)`
  text-transform: uppercase !important;
  background-color: white;
  &:hover {
    background-color: #f6f1f1;
    border: 1px solid #black;
    color:black;
  }
  border : 1px solid rgba(0, 0, 0, 0.30);
`;

const HeaderContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const FilterPadding = styled.div`
    padding: 16px;
`;

const StyledPagination = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-top: 1px solid #eee;
    padding-top: 10px;
`;